import {
  describePlan,
  formatPrice,
  PROMOTION_PLANS,
  PROMOTION_TOWN_KM,
} from '@talkofthetown/shared';
import { useEffect } from 'react';
import { config } from '../config';
import { adsEnabled } from './AdBubble';
import { currencies } from '../hooks/useCurrency';
import { Dialog } from './Dialog';

export type InfoSection =
  'about' | 'how' | 'content' | 'privacy' | 'terms' | 'promote' | 'refunds' | 'contact';

interface Props {
  open: boolean;
  /** Section to scroll to when opening (from the footer links). */
  section: InfoSection;
  onClose: () => void;
}

/** About, how the board works, and the plain-language policies. */
export function InfoDialog({ open, section, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`info-${section}`);
    if (section === 'about') el?.closest('dialog')?.scrollTo({ top: 0 });
    else el?.scrollIntoView({ block: 'start' });
  }, [open, section]);

  return (
    <Dialog open={open} onClose={onClose} labelledBy="info-about" className="info">
      <h2 id="info-about">About Talk of the Town</h2>
      <p>
        One page that shows what people around you are talking about right now. Anyone can post. A
        new post starts as a whisper; once people open it, it gets a voice on the board, and the
        more they open it, the bigger its bubble.
      </p>

      <h3 id="info-how">How the board works</h3>
      <ul>
        <li>
          <strong>Whispers and voices.</strong> New posts are whispers: dotted bubbles that appear
          and disappear all around the middle. Open the ones that catch your eye. Once someone opens
          a post, it’s a voice: a speech bubble in the middle of the board (the five loudest;
          quieter ones whisper). Opening your own post doesn’t count. A voice nobody opens any more
          fades back into a whisper.
        </li>
        <li>
          <strong>Bigger = attention right now.</strong> Every time someone opens a post it gains
          points (once per person per day). Points fade by half every 6 hours, so yesterday’s hit
          makes room for today’s news.
        </li>
        <li>
          <strong>The talk of the town.</strong> The post people are opening most on your board
          right now gets the biggest bubble, in coral. The next loudest are pink, the rest white.
        </li>
        <li>
          <strong>No post can take over.</strong> Size grows slower than points, and no tile can
          take more than a fifth of the board.
        </li>
        <li>
          <strong>The board stays put while you look.</strong> When what it would show changes
          within the distance you picked (new voices, other ranks or sizes), a pulsing ↻ refresh
          button appears at the top. Nothing moves until you press it.
        </li>
        <li>
          <strong>✦ AI.</strong> AI summaries of international news keep every board lively,
          whatever distance you pick. They appear under the publisher’s name, link to the original
          story, get less room than people’s posts and always carry the ✦ mark. The summaries are
          written by AI and can get things wrong, so check the source before relying on them.
        </li>
        <li>
          <strong>Your town.</strong> Look around your own location or pick any town (say, your home
          town while you travel), then how far to look (10 km, 100 km, 1,000 km or the whole world)
          and how far back: posts from the last 6 hours, 24 hours (the default), 3 days or 7 days.
          If nothing’s there, the board says so and offers to look further.
        </li>
        <li>
          <strong>+ more.</strong> When there are more voices than the board can show readably, the
          quietest are gathered in one “+ more” bubble. Tap it to see them all; it also tells you
          when a smaller distance would give them room.
        </li>
      </ul>

      <h3 id="info-content">Content policy</h3>
      <p>
        Post anything worth talking about, but no illegal content, harassment, hate, sexual content,
        violence, spam or scams, and nothing that exposes someone’s private information. Images are
        checked automatically, and posts reported by several people are hidden until reviewed.
        Accounts that break the rules can be banned.
      </p>

      <h3 id="info-privacy">Privacy</h3>
      <p>
        Your location stays on your device and is only used to pick nearby posts. When you post, we
        attach a rough ~5 km area so people nearby see it, never your exact location (if you picked
        a town, the post shows around that town; without either, on the World board). Signing in
        with Google gives us your name and profile photo, which appear on your posts. We count which
        posts were opened, without keeping who opened them for more than two days. Images you upload
        are re-encoded in your browser, which strips camera metadata such as GPS coordinates. Your
        location, town, distance and time choices are remembered in your browser only; we note only
        your device’s time zone, to count visitors by country. Town names come from{' '}
        <a href="https://www.geonames.org/">GeoNames</a> (CC BY 4.0).
      </p>
      {adsEnabled() && (
        <p>
          The board shows one ad, marked “Advertisement”, served by Google AdSense. Google may use
          cookies to choose and measure ads; where the law requires it, you’re asked first. See{' '}
          <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener">
            how Google uses information for ads
          </a>
          .
        </p>
      )}
      {config.payments && (
        <p>
          When you promote a post, Razorpay handles the payment: we never see your card or bank
          details, only that you paid, the amount and Razorpay’s payment reference.
        </p>
      )}

      <h3 id="info-terms">Terms</h3>
      <p>
        You keep ownership of what you post and let us show it on the site. Don’t post what you
        don’t have the right to share. The service is provided as is, may change, and we may remove
        content or accounts that break these rules.
      </p>

      {config.payments && (
        <>
          <h4 id="info-promote">Promoting a post: prices and refunds</h4>
          <p>
            Promoting is optional; reading and posting are free. Anyone signed in can pay to have
            their own post whispered more: a yellow whisper marked <strong>Sponsored</strong> that
            stays on screen longer and comes round more often, around their town (readers within{' '}
            {PROMOTION_TOWN_KM} km of the post) or worldwide. It gets a voice in the middle only the
            way every post does, by being opened. Prices, taxes included:
          </p>
          <ul>
            {PROMOTION_PLANS.map((p) => (
              <li key={p.id}>
                {describePlan(p)}: {currencies.map((c) => formatPrice(p.prices[c], c)).join(' or ')}
              </li>
            ))}
          </ul>
          <p>
            Promoted posts follow the same content policy as every post. We check each one before it
            starts (usually within a day); the time you paid for starts then. It’s a digital
            service: nothing is shipped. If we don’t approve a promoted post, you get a full refund
            to the account you paid from, within 5–7 working days, and before it’s approved you can
            ask us to cancel it for a full refund. Once it has started there’s no refund for the
            time already given; if we remove it for breaking the content policy, or you delete it,
            the rest of its time isn’t refunded. Payments that failed but were charged are refunded
            automatically by Razorpay.
          </p>
        </>
      )}

      <h3 id="info-contact">Contact</h3>
      <p>
        To get something removed quickly, open the post and use <strong>Report</strong>: posts
        reported by several people are hidden right away until reviewed.
        {config.contactEmail && (
          <>
            {' '}
            Questions or takedown requests:{' '}
            <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>
          </>
        )}
      </p>
    </Dialog>
  );
}
